import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Lawyer, Client, Request as ServiceRequest, Order, ListResponse } from '@shawwir/contracts';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Dialog, Input, Spinner, cn } from '@/components/ui';
import { fullName, truncateId } from '@/lib/format';

/**
 * SHL-3: a real global search across lawyers, clients, requests and orders.
 * Only the entities the current user may read are queried, so the palette can
 * never hint at data the server would refuse.
 */

interface Result {
  id: string;
  group: string;
  label: string;
  sublabel?: string;
  to: string;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebounced('');
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const enabled = open && debounced.length >= 2;

  const lawyers = useQuery({
    queryKey: ['search', 'lawyers', debounced],
    queryFn: () => api.list<Lawyer>('lawyers', { q: debounced, pageSize: 5 }),
    enabled: enabled && can('lawyers:read'),
  });
  const clients = useQuery({
    queryKey: ['search', 'clients', debounced],
    queryFn: () => api.list<Client>('clients', { q: debounced, pageSize: 5 }),
    enabled: enabled && can('clients:read'),
  });
  const requests = useQuery({
    queryKey: ['search', 'requests', debounced],
    queryFn: () => api.list<ServiceRequest>('requests', { q: debounced, pageSize: 5 }),
    enabled: enabled && can('requests:read'),
  });
  const orders = useQuery({
    queryKey: ['search', 'orders', debounced],
    queryFn: () => api.list<Order>('orders', { q: debounced, pageSize: 5 }),
    enabled: enabled && can('orders:read'),
  });

  const results = useMemo<Result[]>(() => {
    const out: Result[] = [];
    const push = <T,>(
      query: { data?: ListResponse<T> },
      group: string,
      map: (item: T) => Omit<Result, 'group'>,
    ) => {
      for (const item of query.data?.data ?? []) out.push({ group, ...map(item) });
    };

    push(lawyers, t('nav.lawyers'), (l: Lawyer) => ({
      id: l.id,
      label: fullName(l.party),
      ...(l.party.phone ? { sublabel: l.party.phone } : {}),
      to: `/lawyers/${l.id}`,
    }));
    push(clients, t('nav.clients'), (c: Client) => ({
      id: c.id,
      label: fullName(c.party),
      ...(c.party.phone ? { sublabel: c.party.phone } : {}),
      to: `/clients/${c.id}`,
    }));
    push(requests, t('nav.requests'), (r: ServiceRequest) => ({
      id: r.id,
      label: `${r.type} · ${r.client?.name ?? truncateId(r.id)}`,
      sublabel: r.status,
      to: `/requests/${r.id}`,
    }));
    push(orders, t('nav.orders'), (o: Order) => ({
      id: o.id,
      label: o.client?.name ?? truncateId(o.id),
      sublabel: o.status,
      to: `/orders/${o.id}`,
    }));

    return out;
  }, [lawyers, clients, requests, orders, t]);

  const isFetching =
    lawyers.isFetching || clients.isFetching || requests.isFetching || orders.isFetching;

  const go = (result: Result) => {
    navigate(result.to);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} title={t('common.search')} size="md">
      <div className="flex flex-col gap-2">
        <Input
          autoFocus
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const result = results[activeIndex];
              if (result) go(result);
            }
          }}
          placeholder={t('common.searchPlaceholder')}
          aria-label={t('common.search')}
        />

        {debounced.length < 2 && (
          <p className="p-3 text-center text-xs text-muted">{t('common.searchPlaceholder')}</p>
        )}

        {enabled && isFetching && results.length === 0 && (
          <div className="stack-inline justify-center p-4 text-xs text-muted">
            <Spinner className="h-3 w-3" />
            {t('common.loading')}
          </div>
        )}

        {enabled && !isFetching && results.length === 0 && (
          <p className="p-4 text-center text-xs text-muted">{t('common.noResults')}</p>
        )}

        {results.length > 0 && (
          <ul role="listbox" className="max-h-80 overflow-y-auto">
            {results.map((result, index) => (
              <li key={`${result.group}-${result.id}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => go(result)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded px-2 py-2 text-start text-sm',
                    index === activeIndex ? 'bg-raised' : 'hover:bg-raised',
                  )}
                >
                  <span className="shrink-0 rounded bg-brand/10 px-1.5 py-0.5 text-[10px] text-brand">
                    {result.group}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{result.label}</span>
                  {result.sublabel && (
                    <span className="shrink-0 text-xs text-muted">{result.sublabel}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
